import cx from "classnames";
import type { ReactNode } from "react";

type PdpLayoutProps = {
  navigation: ReactNode;
  gallery: ReactNode;
  heading: ReactNode;
  introduction?: ReactNode;
  overview: ReactNode;
  purchase: ReactNode;
  financing?: ReactNode;
  disclosures: ReactNode;
  benefits?: ReactNode;
  details: ReactNode;
  related?: ReactNode;
};

/**
 * Desktop detail pages split the screen: the photo fills the whole left side, edge to edge and
 * the full height under the header. Once the screen is so large that a half-screen photo would
 * overwhelm the page (desk3xl), the page settles into the header's width and the photo becomes a
 * framed card beside the details.
 */
export const PdpLayout = ({
  navigation,
  gallery,
  heading,
  introduction,
  overview,
  purchase,
  financing,
  disclosures,
  benefits,
  details,
  related,
}: PdpLayoutProps) => (
  <div className="w-full bg-white">
    <div className="desk:grid desk:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] desk:grid-rows-[auto_auto_1fr] desk:items-start desk3xl:mx-auto desk3xl:max-w-6xl desk3xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] desk3xl:gap-x-14 desk3xl:px-8">
      <div
        className={cx(
          // Inset 1rem less than the details, the back button's own inset, so its chevron lines
          // up with the heading; the framed layout has a column gap to nudge into instead.
          "sticky top-0 z-20 bg-white desk:static desk:row-start-1 desk:-mt-2 desk:pl-4 desk:pr-8 desklg:pl-8 desklg:pr-12 deskxl:pl-12 deskxl:pr-16 desk3xl:relative desk3xl:-left-4 desk3xl:px-0",
          gallery ? "desk:col-start-2" : "desk:col-span-2 desk:mx-auto desk:w-full desk:max-w-3xl",
        )}
      >
        {navigation}
      </div>
      {gallery && (
        <div className="min-w-0 desk:sticky desk:top-[65px] desk:col-start-1 desk:row-span-3 desk:row-start-1 desklg:top-[73px] desk3xl:pt-5">
          {gallery}
        </div>
      )}
      <div
        className={cx(
          "min-w-0 px-6 pb-6 pt-6 desk:row-start-2 desk:px-8 desk:pb-8 desk:pt-2 desklg:px-12 desklg:pt-3 deskxl:px-16 desk3xl:px-0",
          gallery ? "desk:col-start-2" : "desk:col-span-2 desk:mx-auto desk:w-full desk:max-w-3xl",
        )}
      >
        {heading}
        {introduction && (
          <div className="mt-3 text-body2 leading-relaxed text-secondary desk:mt-4 desk:text-body1">
            {introduction}
          </div>
        )}
        <div className="mt-4 flex flex-col gap-5">{overview}</div>
        <div className="mt-5">{purchase}</div>
        {financing && <div className="mt-3 w-full text-body3 text-secondary">{financing}</div>}
        {benefits && <div className="mt-6">{benefits}</div>}
      </div>
      <div
        className={cx(
          "min-w-0 space-y-10 px-6 pb-10 desk:row-start-3 desk:space-y-12 desk:px-8 desk:pb-16 desklg:px-12 deskxl:px-16 desk3xl:px-0",
          gallery ? "desk:col-start-2" : "desk:col-span-2 desk:mx-auto desk:w-full desk:max-w-3xl",
        )}
      >
        {details}
        {related && <div className="border-t border-outline-grey pt-10 desk:pt-12">{related}</div>}
      </div>
    </div>
    <div className="border-t border-outline-grey">
      <div className="mx-auto max-w-6xl space-y-4 px-6 py-8 text-body3 text-secondary desk:py-10 desklg:px-8 [&>div>div]:py-0 [&>div>div]:text-left [&>div]:py-0 [&>div]:text-left">
        {disclosures}
      </div>
    </div>
  </div>
);
